'use client';

import { Badge, Tooltip } from '@terra/ui-design-system';
import {
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  PauseCircleIcon,
  type TerraIcon,
} from '@terra/ui-icons';
import { token } from '@terra/ui-styled-system';
import type { MouseEvent } from 'react';

export type OperationStatusBadgeState = 'paused' | 'active' | 'inactive';

const BADGE_STATE_STYLE = {
  paused: {
    bg: token('colors.bg.neutral.warning'),
    fg: token('colors.fg.neutral.warning'),
    border: token('colors.border.neutral.warning'),
  },
  active: {
    bg: token('colors.bg.neutral.success'),
    fg: token('colors.fg.neutral.success'),
    border: token('colors.border.neutral.success'),
  },
  inactive: {
    bg: token('colors.bg.status.duplicate'),
    fg: token('colors.fg.neutral.muted'),
    border: token('colors.border.neutral.subtle'),
  },
} as const satisfies Record<
  OperationStatusBadgeState,
  { bg: string; fg: string; border: string }
>;

const BADGE_STATE_ICON: Record<OperationStatusBadgeState, TerraIcon> = {
  paused: PauseCircleIcon,
  active: CheckmarkCircle02Icon,
  inactive: CancelCircleIcon,
};

type OperationStatusBadgeProps = {
  label: string;
  state: OperationStatusBadgeState;
  tooltip?: string;
  onClick?: () => void;
  onContextMenu?: (event: MouseEvent<HTMLElement>) => void;
};

export function OperationStatusBadge({
  label,
  state,
  tooltip,
  onClick,
  onContextMenu,
}: OperationStatusBadgeProps) {
  const style = BADGE_STATE_STYLE[state];
  const BadgeStateIcon = BADGE_STATE_ICON[state];

  return (
    <Tooltip title={tooltip} positioning={{ strategy: 'fixed' }}>
      <Badge
        size="sm"
        variant="outline"
        whiteSpace="nowrap"
        cursor={onClick ? 'pointer' : undefined}
        onClick={onClick}
        onContextMenu={onContextMenu}
        bg={style.bg}
        color={style.fg}
        borderColor={style.border}
        icon={<BadgeStateIcon size={14} color={style.fg} />}
      >
        {label}
      </Badge>
    </Tooltip>
  );
}
